import { useState, type FormEvent } from 'react'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/shared/ui/Button'
import { Field } from '@/shared/ui/Field'
import { Strip } from '@/shared/ui/Strip'
import { KeyIcon, LockIcon, MailIcon } from '@/shared/ui/icons'
import { PasswordResetStore } from '@/stores/PasswordResetStore'
import { useUi } from '@/stores/RootStore'
import { AuthCard, AuthLayout, authStyles as styles } from './AuthLayout'
import { useMeta } from '@/shared/lib/hooks/useMeta'

/**
 * Восстановление пароля: почта → код → новый пароль.
 *
 * Стор создаётся на этом экране и умирает вместе с ним — разовый токен
 * смены пароля не должен жить дольше, чем нужно.
 */
export const ResetPage = observer(function ResetPage() {
  useMeta('Восстановление пароля')
  const store = useLocalObservable(() => new PasswordResetStore())

  return (
    <AuthLayout>
      {store.stage === 'email' ? <EmailStep store={store} /> : null}
      {store.stage === 'code' ? <CodeStep store={store} /> : null}
      {store.stage === 'password' ? <PasswordStep store={store} /> : null}
      {store.stage === 'done' ? <DoneStep /> : null}
    </AuthLayout>
  )
})

const EmailStep = observer(function EmailStep({ store }: { store: PasswordResetStore }) {
  const [email, setEmail] = useState(store.email)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void store.requestCode(email)
  }

  return (
    <AuthCard
      title="Восстановление"
      sub="Введите почту, привязанную к вашему аккаунту, чтобы восстановить пароль"
    >
      {store.error ? <Strip tone="bad">{store.error}</Strip> : null}
      <form className={styles.form} onSubmit={submit}>
        <Field
          type="email" placeholder="Почта" icon={<MailIcon />}
          value={email} onChange={(e) => setEmail(e.target.value)}
          autoComplete="username" autoFocus required
        />
        <Button type="submit" block busy={store.busy} disabled={!email}>
          Подтвердить
        </Button>
        <Link className={styles.forgot} to="/login" style={{ alignSelf: 'center' }}>
          Вернуться ко входу
        </Link>
      </form>
    </AuthCard>
  )
})

const CodeStep = observer(function CodeStep({ store }: { store: PasswordResetStore }) {
  const [code, setCode] = useState('')

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void store.confirmCode(code)
  }

  return (
    <AuthCard
      title="Подтверждение"
      sub={`Введите код, отправленный на почту ${store.maskedEmail}`}
    >
      {store.error ? <Strip tone="bad">{store.error}</Strip> : null}
      <form className={styles.form} onSubmit={submit}>
        {/* Подписи над полем в макете нет — её роль играет плейсхолдер.
            Читалке имя поля всё равно нужно: `aria-label` остаётся. */}
        <Field
          placeholder="Код подтверждения" aria-label="Код подтверждения"
          icon={<KeyIcon size={24} />}
          value={code} onChange={(e) => setCode(e.target.value)}
          autoComplete="one-time-code" numeric autoFocus required
        />
        <div className={styles.actions}>
          {/* Кнопка активна с самого начала — так она нарисована в макете.
              Пустое поле не даст отправить форму (`required`), а длину
              кода проверяет сервер: гасить кнопку «на глаз» значит
              оставить человека гадать, чего ей не хватает. */}
          <Button type="submit" block busy={store.busy}>Подтвердить</Button>
          <Button type="button" variant="secondary" block onClick={() => store.back()}>
            Отмена
          </Button>
        </div>
      </form>
    </AuthCard>
  )
})

const PasswordStep = observer(function PasswordStep({ store }: { store: PasswordResetStore }) {
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void store.setPassword(password, again)
  }

  // Несовпадение показываем сразу под вторым полем, а не после отправки:
  // человек, набравший пароль дважды по-разному, должен узнать об этом
  // до того, как нажмёт кнопку.
  const mismatch = again.length > 0 && password !== again

  return (
    <AuthCard title="Укажите новый пароль">
      {store.error ? <Strip tone="bad">{store.error}</Strip> : null}
      <form className={styles.form} onSubmit={submit}>
        <Field
          label="Новый пароль" type="password" placeholder="Пароль" icon={<LockIcon />}
          value={password} onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password" autoFocus required
        />
        <Field
          label="Подтвердите пароль" type="password" placeholder="Пароль" icon={<LockIcon />}
          value={again} onChange={(e) => setAgain(e.target.value)}
          autoComplete="new-password" required
          error={mismatch ? 'Пароли не совпадают' : null}
        />
        <Button type="submit" block busy={store.busy}
                disabled={!password || mismatch || !again}>
          Изменить
        </Button>
      </form>
    </AuthCard>
  )
})

function DoneStep() {
  const ui = useUi()
  const navigate = useNavigate()

  return (
    <AuthCard title="Пароль изменён">
      <Button block onClick={() => { ui.success('Пароль изменён'); navigate('/login') }}>
        Войти
      </Button>
    </AuthCard>
  )
}
